import { useMemo } from 'react'
import { useLocation } from 'react-router'
import useSelectedOrganisation from '@/state/entities/organisations/hooks/useSelectedOrganisation'
import { useSelectedVenue } from '@/state/backend/venues/venues.hooks'
import useUser from '@/hooks/useUser'
import { useSupportedDataProviders } from '@/components/marketing/hooks/useSupportedDataProviders'
import { useListSquareIntegrationsQuery } from '@/state/neo/neo-square-auth.api'
import { useHardwareTier } from '@/hooks/use-hardware-tier'
import { useOrderingCapabilities } from '@/hooks/use-ordering-capabilities'
import { useOnboardingSetup } from '@/hooks/use-onboarding-setup'
import { useAccessSubject } from '@/access'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import zionApi from '@/state/zion/zion.slice'
import inboxApi from '@/state/inbox/inbox.slice'
import wifi from '@/state/wifi/wifi.slice'
import type { NavContext, NavItem } from './config'
import { OrgSwitcher } from './org-switcher'
import { SidebarUserFooter } from './sidebar-user-footer'
import { SidebarShellCore } from './sidebar-shell-core'

/**
 * Builds the NavContext for the live (new-UI) nav from the current route —
 * org from the selected organisation, serial parsed from the URL, the resolved
 * venue object, the user email, and the org's supported data providers. The old
 * UI builds its own ctx (with schemeId) via `useNavCtx`; both feed the same
 * `NewSidebarShell`.
 */
export function useNewNavCtx(): NavContext {
  const organisation = useSelectedOrganisation()
  const { pathname } = useLocation()
  const orgId = organisation?.id ?? ''
  // Venue-scoped routes all live under `/:orgId/venues/:serial/...`, so the
  // serial is always at segment index 2 behind `venues` — or absent.
  const segs = pathname.split('/').filter(Boolean)
  const serial =
    segs[1] === 'venues' && typeof segs[2] === 'string' && segs[2].length > 0
      ? segs[2]
      : undefined
  const { data: morpheusVenue } = useSelectedVenue(serial ?? '')
  // Network type (free/paid) is authoritative on the trinity Wi-Fi config —
  // the same source the Wi-Fi settings page edits — so prefer it for the
  // paid-only / free-only Wi-Fi nav gates; fall back to the morpheus venue.
  const { data: wifiConfig } = wifi.useGetSettingsQuery(
    { serial: serial ?? '' },
    { skip: !serial }
  )
  const venue = wifiConfig ?? morpheusVenue
  const user = useUser()
  const { providers: dataProviders, metadata: dataProviderMetadata } =
    useSupportedDataProviders()
  const { tier: hardwareTier, vendorKey } = useHardwareTier(serial)
  // Org-level, not venue-level: Full Bhuna is an org report and is reachable
  // with no venue selected, so a per-venue check would hide it exactly where
  // it is most useful.
  const { data: squareIntegrations } = useListSquareIntegrationsQuery(
    { organizationId: orgId },
    { skip: !orgId }
  )
  // The presence of an integration record IS the connection — there is no
  // active flag on it. Not narrowed to production: an org testing against
  // sandbox still wants to see the report take shape.
  const hasSquare = (squareIntegrations?.integrations?.length ?? 0) > 0
  // Ordering (Mouse) gate. The ONE hook, called from both ctx builders; see
  // `useOrderingCapabilities` for why there is no third copy.
  const { ordering: hasOrdering } = useOrderingCapabilities(orgId || undefined)
  // User-scoped, not org-scoped — the current user's own pending invites to
  // join an organisation, fetched regardless of which org is selected.
  const { data: pendingInvitations } =
    morpheusApi.useGetPendingInvitationsQuery()
  const pendingInvitationsCount = pendingInvitations?.length ?? 0
  // Setup state. THE ONE HOOK, shared with `useNavCtx`; see
  // `useOnboardingSetup` for why there is no second copy.
  //
  // `useSelectedOrganisation` is untyped, so `orgId` is `any`. Narrowed
  // here rather than passed straight on: an `any` crossing into a typed
  // hook is how the hook's signature stops meaning anything.
  const orgIdText: string = typeof orgId === 'string' ? orgId : ''
  const onboarding = useOnboardingSetup(orgIdText || undefined)
  // Resolves every gating axis (role, staff, plan, account shape) so nav items
  // can carry a `capability`. Without this field a gated item fails closed and
  // vanishes — which is why BOTH ctx builders must supply it. `useNavCtx` (old
  // UI) does the same; a signal added to one and not the other is exactly the
  // drift that left `deniedRoles` dead and the v4 nav ungated.
  const access = useAccessSubject()
  return useMemo(
    () => ({
      orgId,
      serial,
      venue,
      userEmail: user?.email,
      dataProviders,
      dataProviderMetadata,
      hardwareTier,
      vendorKey,
      hasSquare,
      hasOrdering,
      pendingInvitationsCount,
      onboardingNav: onboarding.nav,
      onboardingOutstandingCount: onboarding.outstanding,
      access,
    }),
    [
      orgId,
      serial,
      venue,
      user?.email,
      dataProviders,
      dataProviderMetadata,
      hardwareTier,
      vendorKey,
      hasSquare,
      hasOrdering,
      pendingInvitationsCount,
      onboarding.nav,
      onboarding.outstanding,
      access,
    ]
  )
}

/**
 * Customer-facing sidebar shell. A thin wrapper over `SidebarShellCore` that
 * supplies the org-specific chrome: the org switcher header, the org-scoped
 * user footer, and the enquiry/inbox count badges. All the generic drill-in
 * behaviour lives in the core.
 */
export function NewSidebarShell({
  primary,
  secondary,
  ctx,
}: {
  primary: NavItem[]
  secondary: NavItem[]
  ctx: NavContext
}) {
  // Badge counts for sub-nav items. Pending enquiries on the Bookings
  // "Enquiries" item — org-wide or venue-scoped per ctx. Only fetched while
  // there's an org.
  const { data: enquiryCounts } = zionApi.useGetEnquiryCountsQuery(
    { org_id: ctx.orgId, serial: ctx.serial },
    { skip: !ctx.orgId }
  )
  // Inbox unread badge — org-wide total, deliberately unfiltered (no serial /
  // context / assignee / tag) so the count reflects everything unread.
  const { data: inboxUnread } = inboxApi.useGetUnreadCountQuery(
    { orgId: ctx.orgId },
    { skip: !ctx.orgId }
  )
  const badges = useMemo<Record<string, number>>(() => {
    const pending =
      (
        enquiryCounts as
          | { counts?: { by_status?: { pending?: number } } }
          | undefined
      )?.counts?.by_status?.pending ?? 0
    const map: Record<string, number> = {}
    if (pending > 0) {
      // Top-level Bookings row, the Enquiries sub-item, and the Enquiries
      // Inbox sub-sub-item all show the unhandled count.
      map.bookings = pending
      map['bookings-enquiries'] = pending
      map['bookings-enquiries-inbox'] = pending
    }
    const unread = (inboxUnread?.open ?? 0) + (inboxUnread?.closed ?? 0)
    if (unread > 0) {
      // Collapsed accordion shows the count on the Inbox row; expanded, the
      // Conversations child carries it instead.
      map.inbox = unread
      map['inbox-conversations'] = unread
    }
    if (ctx.pendingInvitationsCount) {
      map.invitations = ctx.pendingInvitationsCount
    }
    // How many setup checks are left, on the row that leads to them. The
    // item only exists while this is above zero, so the badge is never a
    // zero and never needs hiding.
    if (ctx.onboardingOutstandingCount) {
      map.onboarding = ctx.onboardingOutstandingCount
    }
    return map
  }, [
    enquiryCounts,
    inboxUnread,
    ctx.pendingInvitationsCount,
    ctx.onboardingOutstandingCount,
  ])

  return (
    <SidebarShellCore
      primary={primary}
      secondary={secondary}
      ctx={ctx}
      badges={badges}
      header={<OrgSwitcher />}
      footer={<SidebarUserFooter orgId={ctx.orgId} />}
    />
  )
}
